import React, { useMemo, useState } from "react";
import { Box, Typography, Popover, Select, MenuItem, Modal, IconButton, Button, Paper, Skeleton } from "@mui/material";
import { parseISO, format } from "date-fns";
import { IoCloseOutline } from "react-icons/io5";
import FilterListIcon from "@mui/icons-material/FilterList";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import { IoFilter, IoFilterOutline } from "react-icons/io5";

import PorterTable from "components/PorterTable";
import GlassCard from "components/GlassCard";
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import VuiPaginationControls from "components/ui/VuiPagination/custom/VuiCustomPagination";
import {
  CONDITION_CATEGORIES,
  CONDITION_CATEGORY_LABELS,
} from "../data/useRDLandscapeData";
import { POPOVER, modalStyle } from "utils/designTokens";
import { COMPONENT_STYLES } from "utils/designTokens";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  if (/^\d{4}-\d{2}$/.test(dateStr)) return parseISO(dateStr + "-01");
  return parseISO(dateStr);
};

const formatDate = (dateStr) => {
  const d = parseDate(dateStr);
  if (!d || isNaN(d.getTime())) return "TBD";
  return format(d, "MMM yyyy");
};

const selectSx = {
  color: "rgba(255,255,255,0.85)",
  fontSize: "0.8rem",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.15)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.3)" },
  "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.5)" },
  minWidth: 200,
};

const menuItemSx = {
  fontSize: "0.8rem",
  color: "rgba(255,255,255,0.85)",
  backgroundColor: "#1A1E3C",
  "&:hover": { backgroundColor: "rgba(87,97,180,0.3)" },
  "&.Mui-selected": { backgroundColor: "rgba(87,97,180,0.45)" },
};

// ---------------------------------------------------------------------------
// Column definitions — no truncation, larger widths
// ---------------------------------------------------------------------------
const TABLE_COLUMNS = [
  { key: "start",                  name: "Start",                   width: "90px",  align: "left",   render: (v) => formatDate(v) },
  { key: "sponsor",                name: "Sponsor",                 width: "180px", align: "left" },
  { key: "disease",                name: "Disease",                 width: "160px", align: "left" },
  { key: "title",                  name: "Title",                   width: "500px", align: "left" },
  { key: "completion",             name: "Completion",              width: "100px", align: "left",   render: (v) => formatDate(v) },
  { key: "enrollment",             name: "Enrollment",              width: "90px",  align: "center" },
  { key: "marketingGoal",          name: "Strategic Goal",          width: "350px", align: "left" },
  { key: "competitiveImplication", name: "Competitive Implication", width: "350px", align: "left" },
  { key: "studyWeakness",          name: "Study Weakness",          width: "350px", align: "left" },
];

// ---------------------------------------------------------------------------
// Disease filter matching (same logic as navbar)
// ---------------------------------------------------------------------------
function matchesDisease(rawConditions, diseaseKey) {
  if (!diseaseKey || diseaseKey === "ALL") return true;
  const terms = CONDITION_CATEGORIES[diseaseKey];
  if (!terms) return true;
  return (
    Array.isArray(rawConditions) &&
    rawConditions.some((c) =>
      terms.some((t) => c.toLowerCase().includes(t.toLowerCase()))
    )
  );
}

// ---------------------------------------------------------------------------
// Filter label
// ---------------------------------------------------------------------------
const filterLabelSx = {
  color: "rgba(255,255,255,0.55)",
  fontSize: "0.7rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  mb: 1,
  display: "block",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const TrialsTable = ({ trials, loading = false }) => {
  const [expandOpen,   setExpandOpen]   = useState(false);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [filters,      setFilters]      = useState({ disease: "ALL", sponsor: "" });
  const [page,         setPage]         = useState(0);
  const [pageSize,     setPageSize]     = useState(10);

  // Build flat row objects
  const allRows = useMemo(() => {
    if (!trials || trials.length === 0) return [];
    return [...trials]
      .sort((a, b) => {
        const dateA = a.last_updated ? parseISO(a.last_updated).getTime() : 0;
        const dateB = b.last_updated ? parseISO(b.last_updated).getTime() : 0;
        return dateB - dateA;
      })
      .map((trial) => ({
        start:                  trial.start_date,
        sponsor:                trial.sponsor || "N/A",
        disease:                trial.conditions?.[0] || "N/A",
        title:                  trial.official_title || "N/A",
        completion:             trial.completion_date,
        enrollment:             trial.enrollment?.toLocaleString() || "N/A",
        marketingGoal:          trial.ai_analysis?.marketing_goal || "N/A",
        competitiveImplication: trial.ai_analysis?.competitive_implications || "N/A",
        studyWeakness:          trial.ai_analysis?.study_weakness || "N/A",
        rawConditions:          trial.conditions || [],
      }));
  }, [trials]);

  const sponsorOptions = useMemo(
    () => [...new Set(allRows.map((r) => r.sponsor).filter((s) => s && s !== "N/A"))],
    [allRows]
  );

  const filteredRows = useMemo(() => {
    return allRows.filter((row) => {
      const diseaseOk = matchesDisease(row.rawConditions, filters.disease);
      const sponsorOk = !filters.sponsor || row.sponsor === filters.sponsor;
      return diseaseOk && sponsorOk;
    });
  }, [allRows, filters]);

  const totalElements = filteredRows.length;
  const totalPages    = Math.max(1, Math.ceil(totalElements / pageSize));
  const pagedRows     = filteredRows.slice(page * pageSize, (page + 1) * pageSize);
  const hasFilters    = filters.disease !== "ALL" || !!filters.sponsor;

  const handleFilterClick     = (e) => setFilterAnchor(e.currentTarget);
  const handleFilterClose     = ()  => setFilterAnchor(null);
  const handleClearFilters    = ()  => { setFilters({ disease: "ALL", sponsor: "" }); setPage(0); };
  const handlePageChange      = (p) => setPage(p);
  const handlePageSizeChange  = (s) => { setPageSize(s); setPage(0); };

  if (!loading && (!trials || trials.length === 0)) return null;

  // ── Shared filter Popover ─────────────────────────────────────────────────
  const filterPopover = (
    <Popover
      open={Boolean(filterAnchor)}
      anchorEl={filterAnchor}
      onClose={handleFilterClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{
        sx: {
          mt: 1,
          overflow: "visible",
          width: 280,
          backgroundColor: POPOVER.background,
          backdropFilter: POPOVER.backdropFilter,
          border: POPOVER.border,
          borderRadius: POPOVER.borderRadius,
          boxShadow: "0 8px 32px 0 rgba(0,0,0,0.3)",
          "&:before": {
            content: '""',
            display: "block",
            position: "absolute",
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: POPOVER.arrowBackground,
            transform: "translateY(-50%) rotate(45deg)",
            zIndex: 0,
            borderLeft: `1px solid ${POPOVER.arrowBorder}`,
            borderTop: `1px solid ${POPOVER.arrowBorder}`,
          },
        },
      }}
    >
      <VuiBox p={2}>
        <Typography sx={filterLabelSx}>Disease</Typography>
        <Select
          value={filters.disease}
          onChange={(e) => { setFilters((f) => ({ ...f, disease: e.target.value })); setPage(0); }}
          size="small"
          variant="outlined"
          fullWidth
          sx={{ ...selectSx, mb: 2 }}
          MenuProps={{ PaperProps: { sx: { backgroundColor: "#1A1E3C" } } }}
        >
          {Object.keys(CONDITION_CATEGORY_LABELS).map((key) => (
            <MenuItem key={key} value={key} sx={menuItemSx}>
              {CONDITION_CATEGORY_LABELS[key]}
            </MenuItem>
          ))}
        </Select>

        <Typography sx={filterLabelSx}>Sponsor</Typography>
        <Select
          value={filters.sponsor}
          onChange={(e) => { setFilters((f) => ({ ...f, sponsor: e.target.value })); setPage(0); }}
          size="small"
          variant="outlined"
          fullWidth
          displayEmpty
          sx={selectSx}
          MenuProps={{ PaperProps: { sx: { backgroundColor: "#1A1E3C" } } }}
        >
          <MenuItem value="" sx={menuItemSx}>All Sponsors</MenuItem>
          {sponsorOptions.map((s) => (
            <MenuItem key={s} value={s} sx={menuItemSx}>{s}</MenuItem>
          ))}
        </Select>

        {hasFilters && (
          <Button
            onClick={handleClearFilters}
            size="small"
            sx={{ mt: 2, color: "rgba(255,255,255,0.5)", fontSize: "0.72rem", textTransform: "none", "&:hover": { color: "#fff" } }}
          >
            Clear filters
          </Button>
        )}
      </VuiBox>
    </Popover>
  );

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.8rem", mb: 1 }}
      >
        Comprehensive view of all tracked clinical studies with AI-driven insights
      </Typography>

      {/* ── Inline table card ──────────────────────────────────────────────── */}
      <GlassCard sx={{ position: "relative", overflow: "visible", borderRadius: "20px" }}>
        <VuiBox pb={0}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <VuiTypography variant="h6" color="white" fontWeight="medium">
              Clinical Trials Overview
            </VuiTypography>
            <VuiBox display="flex" alignItems="center" gap={1}>
              <IconButton
                onClick={handleFilterClick}
                size="small"
                sx={{
                  color: "#c8cfca",
                  backgroundColor: "rgba(30, 30, 42, 0.7)",
                  "&:hover": { backgroundColor: "rgba(50, 50, 62, 0.9)" },
                }}
              >
                {filterAnchor ? <IoFilter /> : <IoFilterOutline />}
              </IconButton>
              <IconButton
                onClick={() => setExpandOpen(true)}
                size="small"
                sx={{
                  color: "#c8cfca",
                  backgroundColor: "rgba(30, 30, 42, 0.7)",
                  "&:hover": { backgroundColor: "rgba(50, 50, 62, 0.9)" },
                }}
              >
                <AspectRatioIcon />
              </IconButton>
            </VuiBox>
          </VuiBox>
        </VuiBox>

        {filterPopover}

        {loading ? (
          <Box px={2} py={1}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Box key={i} display="flex" gap={2} mb={1.5} alignItems="center">
                <Skeleton animation="wave" width="8%" height={16} sx={{ bgcolor: "rgba(255,255,255,0.06)", flexShrink: 0 }} />
                <Skeleton animation="wave" width="14%" height={16} sx={{ bgcolor: "rgba(255,255,255,0.06)", flexShrink: 0 }} />
                <Skeleton animation="wave" width={`${30 + i * 5}%`} height={16} sx={{ bgcolor: "rgba(255,255,255,0.06)" }} />
                <Skeleton animation="wave" width="6%" height={16} sx={{ bgcolor: "rgba(255,255,255,0.06)", flexShrink: 0 }} />
              </Box>
            ))}
          </Box>
        ) : (
          <PorterTable columns={TABLE_COLUMNS} rows={pagedRows} maxHeight="600px" />
        )}

        <VuiPaginationControls
          currentPage={page}
          totalPages={totalPages}
          totalElements={totalElements}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </GlassCard>

      {/* ── Expand Modal — matches digital presence pattern exactly ─────────── */}
      <Modal
        open={expandOpen}
        onClose={() => setExpandOpen(false)}
        aria-labelledby="trials-table-modal-title"
      >
        <Paper sx={modalStyle}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <VuiTypography id="trials-table-modal-title" variant="h6" color="white">
              Clinical Trials Overview
            </VuiTypography>
            <VuiBox>
              <IconButton
                onClick={handleFilterClick}
                size="small"
                sx={{ color: "#c8cfca", mr: 0.5 }}
              >
                <FilterListIcon />
              </IconButton>
              <IconButton
                onClick={() => setExpandOpen(false)}
                size="small"
              >
                <IoCloseOutline color="#c8cfca" size={20} />
              </IconButton>
            </VuiBox>
          </VuiBox>

          <VuiBox sx={{ flexGrow: 1, overflowY: "auto", display: "flex", flexDirection: "column", minHeight: 0 }}>
            <PorterTable columns={TABLE_COLUMNS} rows={filteredRows} maxHeight="fill" />
          </VuiBox>

          <VuiPaginationControls
            currentPage={page}
            totalPages={totalPages}
            totalElements={totalElements}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </Paper>
      </Modal>
    </Box>
  );
};

export default TrialsTable;
